import * as React from "react";
import { RootStore } from "../../../stores/root-store";
import {
    ICmiDocumentationRawi,
    ICmiDocumentationRawiUwe,
    Project,
    UsageProject
} from "../../../stores/project/project";
import { useLoaderData, useNavigate } from "react-router-dom";
import { Icon, Input, Table } from "semantic-ui-react";
import updatedIcon from "../../../assets/images/icons/selected/arrow_circle2.svg";
import actionStatusIcon from "../../../assets/images/icons/plain/clipboard_check_edit.svg";
import balanceIcon from "../../../assets/images/icons/plain/scales.svg";
import delayedIcon from "../../../assets/images/icons/plain/stopwatch.svg";
import SVG from "react-inlinesvg";
import { useItemFilter } from "../../../hooks/use-item-filter";
import ActionStatusIcon from "../../../components/action-status-icon/action-status-icon";
import FilterConfiguration from "../../../components/filter-configuration/filter-configuration";
import { getLabel } from "../../../reflection/reflection";
import { toLocaleString } from "../../../globals/functions";
import GreenRedSignal from "../../../components/green-red-signal/green-red-signal";
import { AppState } from "../../../stores/app-mobx";
import { observer } from "mobx-react";
import { useEffect } from "react";


const filtersKey = "report-usage-projects";
const searchTextKey = "report-usage-projects";
const sortAttributeKey = "report-usage-projects";

type LoadResult = {
    usageProjects: UsageProject[];
    municipalityNames: string[];
    projectTypes: string[];
    status: string[];
    actionStatus: string[];
    appStore: AppState;
};

function projectSearchFilter(project: Project, searchText: string) {
    const searchTextLowerCase = searchText.toLowerCase();

    return (
        project.id.toString().includes(searchTextLowerCase) ||
        project.name.toLowerCase().includes(searchTextLowerCase) ||
        project.municipalityName.toLowerCase().includes(searchTextLowerCase) ||
        project.ebageNr?.toLowerCase().includes(searchTextLowerCase) ||
        ("cmiNumberRawi" in project && (project as ICmiDocumentationRawi).cmiNumberRawi?.toLowerCase().includes(searchTextLowerCase)) ||
        ("cmiDocumentsRawiPre" in project && (
            (project as unknown as ICmiDocumentationRawiUwe).cmiDocumentsRawiPre.map(e => e.toLowerCase()).some(e => e.includes(searchTextLowerCase)) ||
            (project as unknown as ICmiDocumentationRawiUwe).cmiDocumentsRawiApproved.map(e => e.toLowerCase()).some(e => e.includes(searchTextLowerCase)) ||
            (project as unknown as ICmiDocumentationRawiUwe).cmiDocumentsUwePre.map(e => e.toLowerCase()).some(e => e.includes(searchTextLowerCase)) ||
            (project as unknown as ICmiDocumentationRawiUwe).cmiDocumentsUweApproved.map(e => e.toLowerCase()).some(e => e.includes(searchTextLowerCase))
        ))
    );
}

export async function loader(rootStore: RootStore, appStore: AppState): Promise<LoadResult> {
    const rootStoreSession = rootStore.newSession();
    const municipalityNames = (await rootStoreSession.municipalityStore.loadAll()).map(m => m.name);
    await rootStoreSession.projectStore.loadAll();
    await rootStoreSession.areaStore.loadAll();
    await rootStoreSession.compensationStore.loadAll();
    await rootStoreSession.contactStore.loadAll();
    await rootStoreSession.userStore.loadAll();
    await rootStoreSession.activityLogEntryStore.loadAll();
    const usageProjects = rootStoreSession.projectStore.usageProjects;
    const projectTypes = rootStoreSession.keyValueStore.projectTypes.map(i => i.value);
    const status = rootStoreSession.keyValueStore.projectStatuses.map(i => i.value);
    const actionStatus = rootStoreSession.keyValueStore.actionStatus.map(i => i.value);
    return { usageProjects, municipalityNames, projectTypes, status, actionStatus, appStore };
}

const ReportUsageProjects = observer(() => {
    const {
        usageProjects,
        municipalityNames,
        projectTypes,
        status,
        actionStatus,
        appStore
    } = useLoaderData() as LoadResult;
    const filters = appStore.filters.get(filtersKey) ?? [];
    const searchText = appStore.searchTexts.get(searchTextKey) ?? "";
    const sortConfiguration = appStore.sortAttributes.get(sortAttributeKey) ?? {
        attribute: "name",
        direction: "ascending"
    };
    const navigate = useNavigate();
    const [state, dispatch] = useItemFilter<UsageProject>(sortConfiguration.attribute, sortConfiguration.direction, usageProjects, filters);
    const { sortAttribute, sortDirection, items } = state;
    const filterOptions = [
        {
            attribute: "fullType",
            attributeDisplayText: getLabel(UsageProject.prototype, "fullType"),
            values: projectTypes
        },
        {
            attribute: "municipalityName",
            attributeDisplayText: getLabel(UsageProject.prototype, "municipalityName"),
            values: municipalityNames
        },
        {
            attribute: "actionStatusDisplayText",
            attributeDisplayText: getLabel(UsageProject.prototype, "actionStatusDisplayText"),
            values: actionStatus
        },
        {
            attribute: "status",
            attributeDisplayText: getLabel(UsageProject.prototype, "status"),
            values: status
        }
    ];

    useEffect(() => {
        appStore.setSortConfiguration(sortAttributeKey, { attribute: sortAttribute, direction: sortDirection });
    }, [sortAttribute, sortDirection]);

    return (
        <>
            <FilterConfiguration
                filterOptions={filterOptions}
                initialFilters={filters}
                onFilterAdded={(f) => {
                    dispatch({
                        type: "addFilter",
                        filter: f
                    });
                    appStore.addFilter(filtersKey, f);
                }}
                onFilterRemoved={(f) => {
                    dispatch({
                        type: "removeFilter",
                        id: f.id,
                    });
                    appStore.removeFilter(filtersKey, f);
                }}/>
            <div>
                <Input
                    id="search-field"
                    placeholder="Suche nach ID, Name, Gemeinde, Baugesuchsnummer, CMI-Nummer"
                    defaultValue={searchText}
                    type="search"
                    icon={
                        <Icon
                            name="delete"
                            link
                            onClick={() => {
                                const input = document.getElementById(
                                    "search-field"
                                ) as HTMLInputElement;
                                input.value = "";
                                appStore.setSearchText(searchTextKey, "");
                            }}
                        />
                    }
                    onChange={(event) => appStore.setSearchText(searchTextKey, event.target.value)}
                    style={{ width: "800px" }}
                />
                <Table celled={true} collapsing={true} compact="very" selectable={true} singleLine={true} size="small"
                       sortable={true}>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell sorted={sortAttribute === "id" ? sortDirection : null}
                                              onClick={() => dispatch({ type: "changeSort", sortAttribute: "id" })}>
                                {getLabel(UsageProject.prototype, "id")}
                            </Table.HeaderCell>
                            <Table.HeaderCell sorted={sortAttribute === "name" ? sortDirection : null}
                                              onClick={() => dispatch({ type: "changeSort", sortAttribute: "name" })}>
                                {getLabel(UsageProject.prototype, "name")}
                            </Table.HeaderCell>
                            <Table.HeaderCell sorted={sortAttribute === "typeKey" ? sortDirection : null}
                                              onClick={() => dispatch({
                                                  type: "changeSort",
                                                  sortAttribute: "typeKey"
                                              })}>
                                {getLabel(UsageProject.prototype, "fullType")}
                            </Table.HeaderCell>
                            <Table.HeaderCell sorted={sortAttribute === "municipalityName" ? sortDirection : null}
                                              onClick={() => dispatch({
                                                  type: "changeSort",
                                                  sortAttribute: "municipalityName"
                                              })}>
                                {getLabel(UsageProject.prototype, "municipalityName")}
                            </Table.HeaderCell>
                            <Table.HeaderCell sorted={sortAttribute === "status" ? sortDirection : null}
                                              onClick={() => dispatch({ type: "changeSort", sortAttribute: "status" })}>
                                {getLabel(UsageProject.prototype, "status")}
                            </Table.HeaderCell>
                            <Table.HeaderCell sorted={sortAttribute === "ebageNr" ? sortDirection : null}
                                              onClick={() => dispatch({
                                                  type: "changeSort",
                                                  sortAttribute: "ebageNr"
                                              })}>
                                {getLabel(UsageProject.prototype, "ebageNr")}
                            </Table.HeaderCell>
                            <Table.HeaderCell sorted={sortAttribute === "compensationDeadline" ? sortDirection : null}
                                              onClick={() => dispatch({
                                                  type: "changeSort",
                                                  sortAttribute: "compensationDeadline"
                                              })}>
                                {getLabel(UsageProject.prototype, "compensationDeadlineDisplayText")}
                            </Table.HeaderCell>
                            <Table.HeaderCell sorted={sortAttribute === "compensationBalance" ? sortDirection : null}
                                              onClick={() => dispatch({
                                                  type: "changeSort",
                                                  sortAttribute: "compensationBalance"
                                              })}
                                              textAlign="center">
                                <SVG src={balanceIcon} height="auto" width={20}
                                     title={getLabel(UsageProject.prototype, "compensationBalance")}/>
                            </Table.HeaderCell>
                            <Table.HeaderCell sorted={sortAttribute === "actionStatus" ? sortDirection : null}
                                              onClick={() => dispatch({
                                                  type: "changeSort",
                                                  sortAttribute: "actionStatus"
                                              })}
                                              textAlign="center">
                                <SVG src={actionStatusIcon} height="auto" width={20}
                                     title={getLabel(UsageProject.prototype, "actionStatusDisplayText")}/>
                            </Table.HeaderCell>
                            <Table.HeaderCell sorted={sortAttribute === "realizationDelayed" ? sortDirection : null}
                                              onClick={() => dispatch({
                                                  type: "changeSort",
                                                  sortAttribute: "realizationDelayed"
                                              })}
                                              textAlign="center">
                                <SVG src={delayedIcon} height="auto" width={20}
                                     title={getLabel(UsageProject.prototype, "realizationDelayed")}/>
                            </Table.HeaderCell>
                            <Table.HeaderCell sorted={sortAttribute === "updatedAtTimestamp" ? sortDirection : null}
                                              onClick={() => dispatch({
                                                  type: "changeSort",
                                                  sortAttribute: "updatedAtTimestamp"
                                              })}
                                              textAlign="center">
                                <SVG src={updatedIcon} height="auto" width={20}
                                     title={getLabel(UsageProject.prototype, "updatedAt")}/>
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {items.filter(p => projectSearchFilter(p, searchText)).map((p) => (
                            <Table.Row
                                key={p.id}
                                onClick={() => navigate(`/projekte/${p.categoryKey}/${p.id}`)}>
                                <Table.Cell>{p.id}</Table.Cell>
                                <Table.Cell>{p.name}</Table.Cell>
                                <Table.Cell>{p.type}</Table.Cell>
                                <Table.Cell>{p.municipalityName}</Table.Cell>
                                <Table.Cell>{p.status}</Table.Cell>
                                <Table.Cell>{p.ebageNr}</Table.Cell>
                                <Table.Cell>{p.compensationDeadlineDisplayText}</Table.Cell>
                                <Table.Cell textAlign="right">{toLocaleString(p.compensationBalance)}</Table.Cell>
                                <Table.Cell textAlign="center">
                                    <ActionStatusIcon actionStatus={p.actionStatus} issues={p.issues}/>
                                </Table.Cell>
                                <Table.Cell textAlign="center">
                                    {!p.realizationDelayed && <GreenRedSignal greenText="kein Verzug"/>}
                                    {p.realizationDelayed && <GreenRedSignal redText="Umsetzung in Verzug"/>}
                                </Table.Cell>
                                <Table.Cell textAlign="center">{p.updatedAt}</Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </div>
        </>
    );
});

export default ReportUsageProjects;